import { useEffect, useMemo } from 'react';
import { memo, useState } from 'react';

import AbnabatPlayer from '@/js-player/src/components/AbnabatPlayer';
import {
    getImageResizerQueryParam,
    IMAGE_BREAKPOINTS,
    IMAGE_SHAPES,
    IMAGE_SIZES
} from '@/components/dorsaImage/imageTypes';
import { SoundOnIconSvg, SoundOffIconSvg } from '@/assets/Icons';
import useVideo from '@/services/useVideo';
import useUserInteractions from '@/services/useUserInteractions';
import FastUsePlayerDetail from './FastUsePlayerDetail';
import DorsaImage from '@/components/dorsaImage/DorsaImage';

const FastUsePlayer = ({ data, handleVideoOnEnd, selectedContent, muted, setMuted, isInView }) => {
    const [videoDuration, setVideoDuration] = useState(null);
    const [videoPlayedSeconds, setVideoPlayedSeconds] = useState(null);
    const [loading, setLoading] = useState(true);
    const [clicked, setClicked] = useState(true);
    const [showVolumeIcon, setShowVolumeIcon] = useState(true);

    const { media_object, title, id } = data || {};

    const { stream_link, posters, tags } = media_object || {};

    const { setLoggedTimeMutate } = useVideo({
        fetchOnPageLoad: false,
        getMediaLogTime: false,
        id
    });
    const { addViewMutateAsync } = useUserInteractions();

    let showDetailTimeout;

    useEffect(() => {
        showDetailTimeout = setTimeout(() => {
            if (clicked) setClicked(false);
            setShowVolumeIcon(false);
        }, 5000);
        return () => {
            clearInterval(showDetailTimeout);
        };
    }, [clicked]);

    const resetAllSettingsToDefault = () => {
        setVideoDuration(null);
        setVideoPlayedSeconds(0);
        setClicked(true);
        setLoading(true);
        setShowVolumeIcon(true);
    };

    const isActive = useMemo(() => selectedContent.id === id, [selectedContent]);

    // TODO: check and better this part
    useEffect(() => {
        if (!muted) {
            const event = new CustomEvent('somethingPlayed', { detail: { id } });
            window.dispatchEvent(event);
        }
    }, [muted]);


    return (
        <div
            className="fast-use-player-container"
            onClick={() => {
                setShowVolumeIcon(true);
                setMuted(!muted);
            }}>
            {isActive && isInView ? (
                <AbnabatPlayer
                    className="fast-use-player"
                    onEnded={(duration) => {
                        handleVideoOnEnd();
                        setLoggedTimeMutate({
                            time: duration,
                            duration,
                            tags,
                            type: data?.media_type,
                            isLast: true
                        });
                    }}
                    onLoadedData={(duration) => {
                        setVideoDuration(duration);
                        setLoading(false);
                    }}
                    onLoadStartCapture={resetAllSettingsToDefault}
                    isActive={isActive}
                    id={id}
                    src={stream_link}
                    cover={
                        posters?.horizontal_poster +
                        getImageResizerQueryParam({
                            breakpoint: IMAGE_BREAKPOINTS.XS,
                            shape: IMAGE_SHAPES.HORIZONTAL,
                            size: IMAGE_SIZES.SIZE_4
                        })
                    }
                    titles={{
                        primary: title,
                        secondary: ''
                    }}
                    userConfig={{
                        playbackReportInterval: 1
                    }}
                    muted={muted}
                    onPlaybackReport={(duration, seconds) => {
                        setVideoPlayedSeconds(seconds);
                        if (seconds === 3) {
                            addViewMutateAsync({ objectType: data?.media_type, objectId: id, tags });
                        }
                        if (seconds % 10 === 0) {
                            setLoggedTimeMutate({
                                time: seconds,
                                duration,
                                tags,
                                type: data?.media_type
                            });
                        }
                    }}
                    uiConfigType={'fastUse'}
                />
            ) : (
                <DorsaImage
                    src={posters?.horizontal_poster}
                    className={'fast-use-poster'}
                    config={{
                        breakpoint: IMAGE_BREAKPOINTS.XS,
                        shape: IMAGE_SHAPES.HORIZONTAL,
                        size: IMAGE_SIZES.SIZE_4
                    }}
                />
            )}

            <FastUsePlayerDetail
                videoDuration={Math.ceil(videoDuration - videoPlayedSeconds)}
                clicked={clicked}
                loading={loading}
                videoProgress={videoPlayedSeconds / videoDuration}
                title={title}
            />

            {showVolumeIcon && (
                <div className="fast-use-player-sound-container">
                    {muted ? <SoundOffIconSvg /> : <SoundOnIconSvg />}
                </div>
            )}
        </div>
    );
};

export default memo(FastUsePlayer);
